<template>
  <VModal v-if="displayModal" ref="modal" @modal:hidden="$emit('task-modal:closed')">
    <div class="rounded-lg shadow-lg mx-auto h-full">
      <div class="rounded-lg shadow-xs bg-white p-2 h-full flex flex-col overflow-hidden">
        <template v-if="loading">
          <div class="p-6 text-center">
            <Loader />
          </div>
        </template>
        <div v-else class="flex-1 h-0">
          <div class="flex flex-col h-full">
            <div class="flex bg-gray-200 -mx-2 -mt-2 p-2 mb-2 space-x-4">
              <div v-if="client" class="w-10">
                <img
                  v-if="client.logo"
                  :src="client.logo"
                  :alt="client.name"
                  class="rounded-full w-10 h-10 border"
                />
                <div v-else class="rounded-full w-10 h-10 border bg-gray-600" />
              </div>
              <div class="flex-1">
                <h2 v-if="client" class="text-sm">
                  {{ client.name }}
                </h2>
                <div class="flex space-x-2">
                  <h3 v-if="!$route.params.projectId" class="text-xs mt-1">
                    Project:
                    <RouterLink
                      :to="{
                        name: 'accounts.projects.project',
                        params: {
                          accountId: $route.params.accountId || client?.id || project.uuid,
                          projectId: project.uuid,
                        },
                      }"
                    >
                      {{ project.name }}
                    </RouterLink>
                  </h3>
                  <h3 v-if="projectOwner" class="text-xs mt-1">
                    Project Manager:
                    <span class="text-blue-600">{{ projectOwner.fullName }}</span>
                  </h3>
                </div>
              </div>
              <TaskPublish :task="task" />
            </div>
            <div class="flex mb-4">
              <div class="flex space-x-4">
                <TaskStatus
                  :editable="isProjectOwner || $isAdminOrSuperAdmin || isAssignee"
                  :task="task"
                  @status-updated="$emit('status-updated')"
                />
                <TaskAssignee :editable="isProjectOwner || $isAdminOrSuperAdmin" :task="task" />
                <TaskDeadline :editable="isProjectOwner || $isAdminOrSuperAdmin" :task="task" />
                <TaskReview
                  v-if="isProjectOwner || $isAdminOrSuperAdmin"
                  :task="task"
                  :project="project"
                />
              </div>
              <button
                class="ml-auto w-12 h-12 leading-5 hover:bg-gray-200 rounded focus:outline-none focus:bg-blue-600 focus:text-white inline-flex items-center justify-center"
                @click="$refs.modal.close()"
              >
                <svg
                  fill="none"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  class="w-5 h-5 inline-flex"
                >
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div class="flex flex-1 h-0 space-x-2">
              <template
                v-if="
                  data.orderItemId &&
                  ($isAdminOrSuperAdmin || ($currentAccount && $currentAccount.type !== 'provider'))
                "
              >
                <LeftToolbar @order:show="showOrder" />

                <div
                  v-if="leftColumnVisible"
                  class="w-screen max-w-3xl h-full shadow-inner flex flex-col divide-y divide-gray-200 bg-gray-200 rounded overflow-hidden"
                >
                  <div class="flex-1 h-0">
                    <div class="overflow-y-auto h-full p-2">
                      <TaskOrderItem
                        :task="task"
                        :order-item-id="data.orderItemId"
                        :content.sync="data.content"
                      />
                    </div>
                  </div>
                </div>
              </template>

              <div class="w-screen max-w-lg h-full flex flex-col divide-y divide-gray-200 bg-white">
                <div class="flex-1 h-0">
                  <div class="overflow-y-auto h-full">
                    <Task
                      :task="data"
                      :can-edit="isProjectOwner || $isAdminOrSuperAdmin"
                      :submit-status="submitStatus"
                      @update:title="handleUpdateTaskTitle"
                      @update:content="handleUpdateTaskContent"
                      @update:media="handleUpdateTaskMedia"
                    />
                  </div>
                </div>
              </div>

              <template v-if="taskId">
                <div
                  v-if="rightColumnVisible"
                  class="w-screen max-w-md h-full shadow-inner flex flex-col divide-y divide-gray-200 bg-gray-200 rounded overflow-hidden"
                >
                  <div class="flex-1 h-0">
                    <div class="h-full overflow-auto">
                      <component
                        :is="currentRightComponent"
                        :account-id="task.accountUuid"
                        :project="project"
                        :task="task"
                        :on-asset-created="handleAssetCreated"
                        @updated-comments="loadTask({ include: 'commentsCount,assetsCount' })"
                      />
                    </div>
                  </div>
                </div>

                <RightToolbar
                  :has-project="projectId !== 'undefined'"
                  :task="task"
                  @comments:show="showComments"
                  @assets:show="showAssets"
                  @close="$refs.modal.close()"
                />
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </VModal>
</template>

<script>
import { cloneDeep } from 'lodash-es';
import Task from './taskModal/Task.vue';
import LeftToolbar from './taskModal/LeftToolbar.vue';
import RightToolbar from './taskModal/RightToolbar.vue';
import TaskComments from './taskModal/TaskComments.vue';
import AddAssets from './taskModal/AddAssets.vue';
import TaskOrderItem from './taskModal/TaskOrderItem.vue';
import TaskStatus from './taskModal/TaskStatus.vue';
import TaskPublish from './taskModal/TaskPublish.vue';
import TaskAssignee from './taskModal/TaskAssignee.vue';
import TaskDeadline from './taskModal/TaskDeadline.vue';
import TaskReview from './taskModal/TaskReview.vue';

const STATUS_IDLE = 'STATUS_IDLE';
const STATUS_PENDING = 'STATUS_PENDING';
const STATUS_RESOLVED = 'STATUS_RESOLVED';
const STATUS_REJECTED = 'STATUS_REJECTED';

const defaultData = {
  uuid: null,
  title: null,
  content: null,
  dueAt: null,
  hasTime: false,
  status: null,
  userAssignedId: null,
  media: [],
  assets: [],
  accountUuid: null,
  projectId: null,
  orderItemId: null,
};

export default {
  name: 'TaskModal',

  components: {
    Task,
    LeftToolbar,
    RightToolbar,
    TaskComments,
    TaskOrderItem,
    AddAssets,
    TaskStatus,
    TaskPublish,
    TaskAssignee,
    TaskDeadline,
    TaskReview,
  },

  props: {
    taskId: {
      type: String,
      default: null,
    },
    defaultData: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      data: null,
      submitStatus: STATUS_IDLE,
      displayModal: false,
      loading: false,
      leftColumnVisible: false,
      rightColumnVisible: true,
      currentRightComponent: 'TaskComments',
    };
  },

  computed: {
    task() {
      return this.$store.state.entities.tasks.byId[this.taskId];
    },
    userAssigned() {
      return this.$store.state.entities.users.byId[this.task?.userAssigned] || null;
    },
    projectId() {
      return this.task?.projectId || this.task?.project;
    },
    project() {
      if (!this.projectId) {
        return undefined;
      }

      return this.$store.state.entities.projects.byId[this.projectId];
    },
    projectOwner() {
      if (!this.project || !this.project.owner) {
        return null;
      }

      return this.$store.state.entities.users.byId[this.project.ownerId];
    },
    client() {
      return this.$store.state.entities.accounts.byId[this?.project?.client];
    },
    isProjectOwner() {
      return this.project?.ownerId === this.$user.id;
    },
    isAssignee() {
      return this.userAssigned.id === this.$user.id;
    },
    accountRelation() {
      if (!this.project) {
        return null;
      }

      return this.$store.state.entities.accountRelations.byId[this.project.accountRelationUuid];
    },
    projectRoute() {
      return {
        name: 'accounts.projects.project',
        params: {
          accountId: this.accountRelation
            ? this.accountRelation.related_account_uuid
            : this.task.accountUuid,
          projectId: this.projectId,
        },
      };
    },
  },

  watch: {
    task: 'setDefaultData',
  },

  created() {
    this.setDefaultData(this.defaultData);
  },

  mounted() {
    this.displayModal = true;
    if (this.taskId) {
      this.init();
    }
  },

  methods: {
    async init() {
      try {
        this.loading = true;

        await this.loadTask({
          include: [
            'links',
            'notes',
            'media',
            'userAssigned',
            'accountAssigned',
            'contact',
            'orderItem',
            'commentsCount',
            'assetsCount',
            'projectTask',
          ],
        });

        await this.loadProject();

        this.setDefaultRightColumn();

        this.loading = false;
      } catch (error) {
        this.$refs.modal.toggleModal();
      }
    },
    async loadTask(params = {}) {
      try {
        await this.$store.dispatch({
          type: 'ui/tasks/read',
          id: this.taskId,
          params,
        });
      } catch (error) {
        //
      }
    },
    async loadProject() {
      if (!this.task.project && !this.task.projectId) {
        return;
      }

      try {
        await this.$store.dispatch({
          type: 'ui/projects/read',
          projectUuid: this.task.project || this.task.projectId,
          params: {
            include: ['accountRelation', 'client', 'owner'].join(','),
          },
        });
      } catch (error) {
        //
      }
    },
    setDefaultRightColumn() {
      if (this.projectId === 'undefined' || this.task.commentsCount >= this.task.assetsCount) {
        this.currentRightComponent = 'TaskComments';
        return;
      }

      this.currentRightComponent = 'AddAssets';
    },
    setDefaultData(data) {
      const dataWithDefaults = {
        ...cloneDeep(defaultData),
        ...cloneDeep(data),
      };

      const taskData = {
        uuid: dataWithDefaults.uuid,
        title: dataWithDefaults.title,
        content: dataWithDefaults.content,
        dueAt: dataWithDefaults.dueAt,
        hasTime: dataWithDefaults.hasTime,
        status: dataWithDefaults.status,
        userAssignedId: dataWithDefaults.userAssignedId,
        projectId: dataWithDefaults.projectId,
        accountUuid: dataWithDefaults.accountUuid,
        orderItemId: dataWithDefaults.orderItemId,
        media: dataWithDefaults.media.map((idOrMedium) => {
          if (typeof idOrMedium === 'string') {
            return this.$store.state.entities.media.byId[idOrMedium];
          }
          return idOrMedium;
        }),
        assets: dataWithDefaults.assets.map((idOrAsset) => {
          if (typeof idOrAsset === 'string') {
            return this.$store.state.entities.assets.byId[idOrAsset];
          }
          return idOrAsset;
        }),
      };

      this.data = {
        ...defaultData,
        ...taskData,
      };
    },
    showOrder() {
      if (this.leftColumnVisible) {
        this.leftColumnVisible = false;
      } else {
        this.rightColumnVisible = false;
        this.leftColumnVisible = true;
      }
    },
    showComments() {
      this.setLeftColumnVisible(false);
      this.setRightColumnVisible(true);
      this.setRightComponent('TaskComments');
    },
    showAssets() {
      this.setLeftColumnVisible(false);
      this.setRightColumnVisible(true);
      this.setRightComponent('AddAssets');
    },
    setRightComponent(component) {
      this.currentRightComponent = component;
    },
    setLeftColumnVisible(visible) {
      if (this.leftColumnVisible === visible) {
        return;
      }

      this.leftColumnVisible = visible;
    },
    setRightColumnVisible(visible) {
      if (this.rightColumnVisible === visible) {
        return;
      }

      this.rightColumnVisible = visible;
    },
    submitTask() {
      if (this.taskId) {
        this.updateTask();
        return;
      }

      this.storeTask();
    },
    async updateTask() {
      if (this.submitStatus === STATUS_PENDING) {
        return;
      }

      this.submitStatus = STATUS_PENDING;

      try {
        await this.$store.dispatch({
          type: 'ui/tasks/update',
          taskUuid: this.taskId,
          formData: this.data,
        });

        this.$emit('task:updated');

        this.submitStatus = STATUS_RESOLVED;
      } catch (error) {
        // should handle 244 errors
        this.submitStatus = STATUS_REJECTED;
      } finally {
        setTimeout(() => {
          this.submitStatus = STATUS_IDLE;
        }, 1000);
      }
    },
    async storeTask() {
      if (this.submitStatus === STATUS_PENDING) {
        return;
      }

      this.submitStatus = STATUS_PENDING;

      try {
        const { taskUuid } = await this.$store.dispatch({
          type: 'ui/tasks/store',
          body: this.data,
        });

        this.$emit('task:created', taskUuid);

        this.submitStatus = STATUS_RESOLVED;
      } catch (error) {
        // should handle 244 errors
        this.submitStatus = STATUS_REJECTED;
      } finally {
        setTimeout(() => {
          this.submitStatus = STATUS_IDLE;
        }, 1000);
      }
    },
    // eslint-disable-next-line no-unused-vars
    handleAssetCreated(assetId) {
      // todo
    },
    handleUpdateTaskTitle(newValue) {
      this.data.title = newValue;
      this.submitTask();
    },
    handleUpdateTaskContent(newValue) {
      this.data.content = newValue;
      this.submitTask();
    },
    handleUpdateTaskMedia(newValue) {
      this.data.media = newValue;
    },
  },

  provide() {
    return {
      refreshTask: this.loadTask,
    };
  },
};
</script>
